// ===========================================================================
// DistributionDonut — donut interactivo reutilizable (Networks y Time Since).
//
// Cada segmento es clickeable: dispara onSelect(value). El segmento activo se
// resalta (opacidad de los demás baja). La leyenda lateral muestra conteo y
// porcentaje, y también es clickeable. Si solo hay 1 categoría se pinta sólido.
// ===========================================================================
import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
} from "recharts";
import { formatNumber, formatShare } from "../../utils/format";

export default function DistributionDonut({
  title,
  ariaLabel,
  data = [],
  activeValue = null,
  onSelect,
}) {
  const rows = Array.isArray(data) ? data : [];
  const total = rows.reduce((acc, r) => acc + (Number(r.count) || 0), 0);

  const handleClick = (value) => {
    if (!onSelect) return;
    // Click en el segmento activo → limpia ese filtro (toggle).
    onSelect(activeValue === value ? null : value);
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}
      >
        {title}
      </Typography>

      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No data.
        </Typography>
      ) : (
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
          <Box sx={{ width: 96, height: 96, flexShrink: 0 }} aria-label={ariaLabel} role="img">
            <ResponsiveContainer>
              <PieChart>
                <RechartsTooltip
                  formatter={(value, name) => [
                    `${formatNumber(value)} (${formatShare(value, total)})`,
                    name,
                  ]}
                />
                <Pie
                  data={rows}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius={26}
                  outerRadius={46}
                  paddingAngle={rows.length > 1 ? 2 : 0}
                  isAnimationActive={false}
                  onClick={(entry) => handleClick(entry?.value)}
                >
                  {rows.map((entry) => {
                    const dim = activeValue && activeValue !== entry.value;
                    return (
                      <Cell
                        key={entry.value}
                        fill={entry.color}
                        opacity={dim ? 0.3 : 1}
                        stroke={activeValue === entry.value ? "#111827" : "none"}
                        strokeWidth={activeValue === entry.value ? 2 : 0}
                        style={{ cursor: "pointer" }}
                      />
                    );
                  })}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Box>

          <Stack spacing={0.2} sx={{ minWidth: 0, flex: 1, maxHeight: 110, overflowY: "auto" }}>
            {rows.map((entry) => {
              const active = activeValue === entry.value;
              return (
                <Stack
                  key={entry.value}
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  onClick={() => handleClick(entry.value)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1,
                    px: 0.5,
                    py: 0.1,
                    bgcolor: active ? "action.selected" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Box sx={{ width: 9, height: 9, borderRadius: "2px", bgcolor: entry.color, flexShrink: 0 }} />
                  <Typography variant="caption" noWrap sx={{ flex: 1, fontWeight: active ? 700 : 400 }}>
                    {entry.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {formatNumber(entry.count)}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
